import { roleConfigAggregationRuleHeight } from '../components/tables/playerSimpleTable/RoleConfigTable';
import { InitialTeamDataPoint, TeamDataPoint } from '../components/teamView/TeamView';


interface TableNode extends TeamDataPoint {
  leftPx: number;
  topPx: number;
  originalLeftPx: number;
  originalTopPx: number;
  isCentered: boolean;
  movementResistance: {
    horizontal: number;
    vertical: number;
  };
  hasSafeMargin: boolean;
  hasDoubleSafeMargin: boolean;
}


interface NodeMovement {
  leftDelta: number;
  topDelta: number;
  heightDecrease: boolean;
}


export const resolveTableCollisions = (
  nodes: InitialTeamDataPoint[],
  tableWidth: number,
  tableRowHeight: number,
  containerWidth: number,
  containerHeight: number,
  isRoleConfigTable: boolean,

  MAX_ITERATIONS: number = 50,
  MAX_POSITION_DEVIATION: number = 10,
  MOMENTUM_FACTOR: number = 0.6,
  HEIGHT_CAPPING_FREQUENCY: number = 2,
  HEIGHT_CAPPING_STARTING_ITERATION: number = 5,
  SOFT_MARGIN: number = 8,
  SOFT_MARGIN_EDGES: number = 10,
  SAFE_MARGIN: number = 10,
): TeamDataPoint[] => {

  const updatedNodes = [...nodes];

  let hasCollisions = true; // if a collision is detected, we apply movement and must run again
  let relaxationApplied = false; // if a relaxation is applied, it is applied at the and, and this may cause a collision, so we must run again
  let iterations = 0;

  let iterationWasStable = false;

  // convert percentage positions to pixels and initialize table properties, considering the container bounds constraints
  const nodesInPixels: TableNode[] = updatedNodes.map(node => {
    const width = tableWidth;
    let height = tableRowHeight * Math.max(node.playerList.length, 1); // if table is empty, we set minimum height to 1 row (drop zone)
    if (isRoleConfigTable && node.playerList.length > 1) {
      height += roleConfigAggregationRuleHeight;
    }

    // enforce container bounds in pixels
    const minXPx = width / 2 + SOFT_MARGIN_EDGES;
    const maxXPx = containerWidth - width / 2 - SOFT_MARGIN_EDGES;
    const minYPx = height / 2 + SOFT_MARGIN_EDGES;
    const maxYPx = containerHeight - height / 2 - SOFT_MARGIN_EDGES;

    const isCentered = Math.abs(node.left - 50) < 1;
    const originalLeftPx = (node.left / 100) * containerWidth;
    const leftPx = isCentered
      ? originalLeftPx
      : Math.max(minXPx, Math.min(maxXPx, originalLeftPx));

    const originalTopPx = (node.top / 100) * containerHeight;
    const topPx = Math.max(minYPx, Math.min(maxYPx, originalTopPx));

    return {
      ...node,
      leftPx,
      topPx,
      originalLeftPx,
      originalTopPx,
      isCentered,
      width,
      height,
      desiredHeight: height,
      movementResistance: {
        horizontal: 1,
        vertical: 1
      },
      hasSafeMargin: true,
      hasDoubleSafeMargin: true,
    };
  });


  while ((hasCollisions || relaxationApplied) && iterations < MAX_ITERATIONS) {

    hasCollisions = false;
    relaxationApplied = false;
    iterations++;

    // initialize movement tracking for all nodes
    const nodeMovements: NodeMovement[] = nodesInPixels.map(() => ({
      leftDelta: 0,
      topDelta: 0,
      heightDecrease: false
    }));

    // update movement resistance based on deviation
    for (let i = 0; i < nodesInPixels.length; i++) {
      const node = nodesInPixels[i];
      const horizontalDeviation = Math.abs(node.leftPx - node.originalLeftPx) / containerWidth * 100;
      const verticalDeviation = Math.abs(node.topPx - node.originalTopPx) / containerHeight * 100;

      if (MAX_POSITION_DEVIATION === 0) {
        node.movementResistance = { horizontal: 0, vertical: 0 };
      }
      else {
        node.movementResistance = {
          horizontal: horizontalDeviation >= MAX_POSITION_DEVIATION ? 0 : 1 - (horizontalDeviation / MAX_POSITION_DEVIATION),
          vertical: verticalDeviation >= MAX_POSITION_DEVIATION ? 0 : 1 - (verticalDeviation / MAX_POSITION_DEVIATION)
        };
      }

      // check for safe margins against edges
      setInitialSafeMargins(node, containerWidth, containerHeight, SOFT_MARGIN_EDGES, SAFE_MARGIN);
    }


    // check for collisions
    for (let i = 0; i < nodesInPixels.length; i++) {
      const nodeA = nodesInPixels[i];

      // check collisions with other nodes
      for (let j = i + 1; j < nodesInPixels.length; j++) {
        const nodeB = nodesInPixels[j];

        // calculate overlap
        const horizontalOverlap = (nodeA.width + nodeB.width) / 2 + SOFT_MARGIN - Math.abs(nodeA.leftPx - nodeB.leftPx);
        const verticalOverlap = (nodeA.height + nodeB.height) / 2 + SOFT_MARGIN - Math.abs(nodeA.topPx - nodeB.topPx);

        if (horizontalOverlap > 0 && verticalOverlap > 0) {
          hasCollisions = true;

          // every HEIGHT_CAPPING_FREQUENCY iteration, we allow to cap the table height of the most appropriate node
          const heightCappingCouldHelp = horizontalOverlap / verticalOverlap > 0.001;
          if (
            !isRoleConfigTable &&
            heightCappingCouldHelp &&
            iterations > HEIGHT_CAPPING_STARTING_ITERATION &&
            iterations % HEIGHT_CAPPING_FREQUENCY === 0
          ) {
            // only consider height reduction if table has more than 2 rows and the node has not already been capped
            const nodeAHeightCanBeCapped = nodeA.height > tableRowHeight * 2;
            const nodeBHeightCanBeCapped = nodeB.height > tableRowHeight * 2;

            if (nodeAHeightCanBeCapped || nodeBHeightCanBeCapped) {

              // we prefer to cap the taller node, but if they have the same height, we cap the node with the most players
              const shouldCapNodeA = nodeAHeightCanBeCapped && (
                nodeA.height > nodeB.height || (nodeA.height === nodeB.height && nodeA.playerList.length >= nodeB.playerList.length)
              );

              if (shouldCapNodeA) {
                nodeMovements[i].heightDecrease = true;
              }
              else {
                nodeMovements[j].heightDecrease = true;
              }
            }
          }

          // calculate movements - we only move in one direction, based on the relative size of the overlap, unless the last iteration was stable
          if (horizontalOverlap <= verticalOverlap || iterationWasStable) {

            // if we are closer to resolve the collision in X, we move in X direction
            const moveX = horizontalOverlap * MOMENTUM_FACTOR;
            const direction = Math.sign(nodeB.leftPx - nodeA.leftPx);

            // if we are very close to resolving the collision, we don't apply resistance, to avoid asymptotical behavior
            // if we are moving towards the original position, we don't apply resistance
            const weAreCloseToResolve = horizontalOverlap < 0.01;
            const nodeAIsMovingTowardsOriginalPosition = direction > 0
              ? nodeA.leftPx < nodeA.originalLeftPx
              : nodeA.leftPx > nodeA.originalLeftPx;
            const nodeBIsMovingTowardsOriginalPosition = direction < 0
              ? nodeB.leftPx < nodeB.originalLeftPx
              : nodeB.leftPx > nodeB.originalLeftPx;

            const resistanceA = weAreCloseToResolve || nodeAIsMovingTowardsOriginalPosition ? 1 : nodeA.movementResistance.horizontal;
            const resistanceB = weAreCloseToResolve || nodeBIsMovingTowardsOriginalPosition ? 1 : nodeB.movementResistance.horizontal;

            if (!nodeA.isCentered && !nodeB.isCentered) {
              // both nodes can move, split the movement
              nodeMovements[i].leftDelta -= direction * moveX * 2 * resistanceA;
              nodeMovements[j].leftDelta += direction * moveX * 2 * resistanceB;
            }
            else if (nodeA.isCentered) {
              // only B can move, move full distance, to avoid asymptotical behavior
              nodeMovements[j].leftDelta += direction * horizontalOverlap * resistanceB;
            }
            else {
              // only A can move, move full distance, to avoid asymptotical behavior
              nodeMovements[i].leftDelta -= direction * horizontalOverlap * resistanceA;
            }
          }

          if (horizontalOverlap > verticalOverlap || iterationWasStable) {
            // if we are closer to resolve the collision in Y, we move in Y direction
            const moveY = verticalOverlap > 0 ? verticalOverlap * MOMENTUM_FACTOR : 0;
            const direction = Math.sign(nodeB.topPx - nodeA.topPx);

            // if we are very close to resolving the collision, we don't apply resistance, to avoid asymptotical behavior
            // if we are moving towards the original position, we don't apply resistance
            const weAreCloseToResolve = verticalOverlap < 0.01;
            const nodeAIsMovingTowardsOriginalPosition = direction > 0
              ? nodeA.topPx < nodeA.originalTopPx
              : nodeA.topPx > nodeA.originalTopPx;
            const nodeBIsMovingTowardsOriginalPosition = direction < 0
              ? nodeB.topPx < nodeB.originalTopPx
              : nodeB.topPx > nodeB.originalTopPx;

            const resistanceA = weAreCloseToResolve || nodeAIsMovingTowardsOriginalPosition ? 1 : nodeA.movementResistance.vertical;
            const resistanceB = weAreCloseToResolve || nodeBIsMovingTowardsOriginalPosition ? 1 : nodeB.movementResistance.vertical;

            nodeMovements[i].topDelta -= direction * moveY * resistanceA;
            nodeMovements[j].topDelta += direction * moveY * resistanceB;
          }
        }

        setSafeMarginsForNodes(nodeA, nodeB, SOFT_MARGIN, SAFE_MARGIN);
      }
    }

    iterationWasStable = true;

    // apply all movements
    for (let i = 0; i < nodesInPixels.length; i++) {
      const node = nodesInPixels[i];
      const movement = nodeMovements[i];

      // apply horizontal movement
      if (!node.isCentered) {
        const minXPx = tableWidth / 2 + SOFT_MARGIN_EDGES;
        const maxXPx = containerWidth - tableWidth / 2 - SOFT_MARGIN_EDGES;

        const newLeftPx = Math.max(minXPx, Math.min(maxXPx, node.leftPx + movement.leftDelta));
        if (newLeftPx !== node.leftPx) {
          iterationWasStable = false;
        }
        node.leftPx = newLeftPx;
      }

      // apply vertical movement
      const minYPx = node.height / 2 + SOFT_MARGIN_EDGES;
      const maxYPx = containerHeight - node.height / 2 - SOFT_MARGIN_EDGES;

      const newTopPx = Math.max(minYPx, Math.min(maxYPx, node.topPx + movement.topDelta));
      if (newTopPx !== node.topPx) {
        iterationWasStable = false;
      }
      node.topPx = newTopPx;

      // apply height decrease if needed, but never below 2 rows
      if (movement.heightDecrease && node.height > tableRowHeight * 2) {
        iterationWasStable = false;
        node.height -= tableRowHeight;

        // try to move back towards original position if we've moved vertically
        const verticalDeviation = node.topPx - node.originalTopPx;
        if (Math.abs(verticalDeviation) > 0) {
          // move back to original position, but not more than one table row height
          const moveBackAmount = Math.min(verticalDeviation, tableRowHeight);
          const minYPx = node.height / 2 + SOFT_MARGIN_EDGES;
          const maxYPx = containerHeight - node.height / 2 - SOFT_MARGIN_EDGES;
          node.topPx = Math.max(minYPx, Math.min(maxYPx, node.topPx - moveBackAmount));
        }
      }

      // apply safe margin relaxation if possible
      if (node.hasSafeMargin) {
        // move half the safe margin towards the original position
        const verticalDeviation = node.topPx - node.originalTopPx;
        const horizontalDeviation = node.leftPx - node.originalLeftPx;

        if (verticalDeviation !== 0 || horizontalDeviation !== 0) {
          relaxationApplied = true;

          // we try to be aggressive with the safe margin
          const preferedDistance = node.hasDoubleSafeMargin ? SAFE_MARGIN * 2 : SAFE_MARGIN;

          // move back towards original position
          const verticalMoveBack = Math.min(Math.abs(verticalDeviation), preferedDistance / 2) * Math.sign(verticalDeviation);
          node.topPx -= verticalMoveBack;

          if (verticalMoveBack !== 0) {
            iterationWasStable = false;
          }

          if (!node.isCentered) {
            const horizontalMoveBack = Math.min(Math.abs(horizontalDeviation), preferedDistance / 2) * Math.sign(horizontalDeviation);
            node.leftPx -= horizontalMoveBack;

            if (horizontalMoveBack !== 0) {
              iterationWasStable = false;
            }
          }
        }
      }
    }
  }

  // convert back to percentages for rendering
  return nodesInPixels.map(node => {
    return {
      ...node,
      left: (node.leftPx / containerWidth) * 100,
      top: (node.topPx / containerHeight) * 100,
    };
  });
};


// check if we have safe margins to the edges in the direction of the original position
const setInitialSafeMargins = (
  node: TableNode,
  containerWidth: number,
  containerHeight: number,
  SOFT_MARGIN_EDGES: number,
  SAFE_MARGIN: number,
) => {

  // determine movement direction
  const isMovingLeft = !node.isCentered && node.leftPx > node.originalLeftPx;
  const isMovingUp = node.topPx > node.originalTopPx;

  let hasSafeMargin = true;
  let hasDoubleSafeMargin = true;

  if (!node.isCentered) {
    const leftPxOfEdgeInDirection = isMovingLeft ? 0 : containerWidth;
    const safeMarginOverlapXToEdgeInDirection = (node.width / 2 + SOFT_MARGIN_EDGES + SAFE_MARGIN) - Math.abs(node.leftPx - leftPxOfEdgeInDirection);
    const doubleSafeMarginOverlapXToEdgeInDirection = (node.width / 2 + SOFT_MARGIN_EDGES + SAFE_MARGIN * 2) - Math.abs(node.leftPx - leftPxOfEdgeInDirection);

    if (safeMarginOverlapXToEdgeInDirection > 0) {
      hasSafeMargin = false;
    }
    if (doubleSafeMarginOverlapXToEdgeInDirection > 0) {
      hasDoubleSafeMargin = false;
    }
  }

  const topPxOfEdgeInDirection = isMovingUp ? 0 : containerHeight;
  const safeMarginOverlapYToEdgeInDirection = (node.height / 2 + SOFT_MARGIN_EDGES + SAFE_MARGIN) - Math.abs(node.topPx - topPxOfEdgeInDirection);
  const doubleSafeMarginOverlapYToEdgeInDirection = (node.height / 2 + SOFT_MARGIN_EDGES + SAFE_MARGIN * 2) - Math.abs(node.topPx - topPxOfEdgeInDirection);

  if (safeMarginOverlapYToEdgeInDirection > 0) {
    hasSafeMargin = false;
  }
  if (doubleSafeMarginOverlapYToEdgeInDirection > 0) {
    hasDoubleSafeMargin = false;
  }

  node.hasSafeMargin = hasSafeMargin;
  node.hasDoubleSafeMargin = hasDoubleSafeMargin;
};


// check if we have safe margin to any table or edge in the direction of the original position
const setSafeMarginsForNodes = (
  nodeA: TableNode,
  nodeB: TableNode,
  SOFT_MARGIN: number,
  SAFE_MARGIN: number,
) => {

  // check if there's any overlap at all
  const safeMarginOverlapX = (nodeA.width + nodeB.width) / 2 + SOFT_MARGIN + SAFE_MARGIN - Math.abs(nodeA.leftPx - nodeB.leftPx);
  const safeMarginOverlapY = (nodeA.height + nodeB.height) / 2 + SOFT_MARGIN + SAFE_MARGIN - Math.abs(nodeA.topPx - nodeB.topPx);
  const doubleSafeMarginOverlapX = (nodeA.width + nodeB.width) / 2 + SOFT_MARGIN + SAFE_MARGIN * 2 - Math.abs(nodeA.leftPx - nodeB.leftPx);
  const doubleSafeMarginOverlapY = (nodeA.height + nodeB.height) / 2 + SOFT_MARGIN + SAFE_MARGIN * 2 - Math.abs(nodeA.topPx - nodeB.topPx);

  // for node A, we want to check the direction towards node b in both x and y, and the direction towards the original position in both x and y
  // if either the x direction here is the same, or the y direction here is the same, we set safe margin to false
  const directionToB = {
    x: Math.sign(nodeB.leftPx - nodeA.leftPx),
    y: Math.sign(nodeB.topPx - nodeA.topPx)
  };
  const directionToOriginal = {
    x: Math.sign(nodeA.originalLeftPx - nodeA.leftPx),
    y: Math.sign(nodeA.originalTopPx - nodeA.topPx)
  };

  if (directionToB.x === directionToOriginal.x || directionToB.y === directionToOriginal.y) {
    if (safeMarginOverlapX > 0 && safeMarginOverlapY > 0) {
      nodeA.hasSafeMargin = false;
    }
    if (doubleSafeMarginOverlapX > 0 && doubleSafeMarginOverlapY > 0) {
      nodeA.hasDoubleSafeMargin = false;
    }
  }

  // same for node B
  const directionToA = {
    x: Math.sign(nodeA.leftPx - nodeB.leftPx),
    y: Math.sign(nodeA.topPx - nodeB.topPx)
  };
  const directionToOriginalB = {
    x: Math.sign(nodeB.originalLeftPx - nodeB.leftPx),
    y: Math.sign(nodeB.originalTopPx - nodeB.topPx)
  };

  if (directionToA.x === directionToOriginalB.x || directionToA.y === directionToOriginalB.y) {
    if (safeMarginOverlapX > 0 && safeMarginOverlapY > 0) {
      nodeB.hasSafeMargin = false;
    }
    if (doubleSafeMarginOverlapX > 0 && doubleSafeMarginOverlapY > 0) {
      nodeB.hasDoubleSafeMargin = false;
    }
  }
};
